// src/shared/components/ui/LineGraph.tsx
import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
} from 'recharts'

const DEFAULT_PALETTE = [
  '#82C3EC', '#A8D5BA', '#F5CDA7', '#D6A4E7',
  '#F7A6A6', '#F9E79F', '#B9AEDC', '#AED6F1',
]

interface LineGraphProps<T extends Record<string, any>> {
  data: T[]
  xKey: keyof T
  yKeys: Array<keyof T>
  /** If 'hour', place a tick every 6 points and use special formatting */
  unit?: 'hour' | 'day' | string
  labels?: string[]
  width?: string | number
  height?: number
  showDots?: boolean
  colors?: string[]
  showGrid?: boolean
  showLegend?: boolean
}

export default function LineGraph<T extends Record<string, any>>({
  data,
  xKey,
  yKeys,
  unit,
  labels,
  width = '100%',
  height = 200,
  showDots = false,
  colors,
  showGrid = true,
  showLegend = false,
}: LineGraphProps<T>) {
  const isHourly = unit === 'hour'

  // every 6 points if hourly, otherwise auto
  const xInterval = isHourly ? 6 : 'auto'

  // for hourly: at midnight show full date; else show HH:mm
  const formatX = (val: any) => {
    if (!isHourly) return String(val)
    const dt = dayjs(val)
    return dt.hour() === 0 && dt.minute() === 0
      ? dt.format('YYYY-MM-DD')
      : dt.format('HH:mm')
  }

  // choose palette (either user‐provided or default)
  const palette = useMemo(
    () =>
      colors && colors.length >= yKeys.length
        ? colors
        : DEFAULT_PALETTE,
    [colors, yKeys.length]
  )

  return (
    <div style={{ width, height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          {showGrid && (
            <CartesianGrid stroke="#374151" strokeDasharray="3 3" />
          )}

          <XAxis
            dataKey={String(xKey)}
            interval={xInterval as any}
            tickLine={false}
            axisLine={{ stroke: '#4b5563' }}
            tick={{ fill: '#9ca3af', fontSize: 12 }}
            tickFormatter={formatX}
          />

          <YAxis
            tickLine={false}
            axisLine={{ stroke: '#4b5563' }}
            tick={{ fill: '#9ca3af', fontSize: 12 }}
            label={{
              value:
                labels && labels.length === 1 ? labels[0] : 'Value',
              angle: -90,
              position: 'insideLeft',
              fill: '#9ca3af',
              fontSize: 12,
            }}
            domain={['auto', 'auto']}
          />

          <Tooltip
            contentStyle={{
              backgroundColor: '#1f2937',
              border: 'none',
              borderRadius: 4,
              color: '#f9fafb',
              fontSize: 12,
            }}
            labelFormatter={String}
          />

          {showLegend && (
            <Legend
              wrapperStyle={{ color: '#9ca3af', fontSize: 12 }}
            />
          )}

          {yKeys.map((yKey, idx) => (
            <Line
              key={String(yKey)}
              type="monotone"
              dataKey={String(yKey)}
              stroke={palette[idx % palette.length]}
              strokeWidth={2}
              connectNulls
              dot={{
                r: showDots ? 4 : 0,
                stroke: palette[idx % palette.length],
                strokeWidth: showDots ? 2 : 0,
                fill: 'transparent',
              }}
              activeDot={{
                r: showDots ? 6 : 0,
                stroke: palette[idx % palette.length],
                strokeWidth: showDots ? 3 : 0,
                fill: 'transparent',
              }}
              name={labels?.[idx] ?? String(yKey)}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
